import EspFirmwareCheck from "./EspFirmwareCheck";
import "./EspFirmwares.scss";
import {useEffect, useMemo, useState} from "react";
import {Button, Input, InputGroup, Pagination, RadioTile, RadioTileGroup, Table, toaster, Tooltip, Whisper} from "rsuite";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import {
  AiOutlineCloseIcon,
  AiOutlineSearchIcon,
  FaPlusIcon,
} from "../../../models/Icons.model";
import {ESP_TYPE_OPTIONS, EspFirmwareItem, EspFirmwaresResponse, FIRMWARE_TABLE_HEIGHT, PAGE_SIZE} from "../Firmwares.model";
import {FRONT_FILTER_PAGE_SIZE, FirmwaresUtils} from "../Firmwares.util";
import NotificationFactory from "../../../shared/Notifications";
import EspFirmwareModal from "./EspFirmwareModal";
import FirmwareDescriptionCell from "./FirmwareDescriptionCell";

const {Column, HeaderCell, Cell} = Table;

const EspFirmwares = () => {
  const [loading, setLoading] = useState(false);
  const [firmwares, setFirmwares] = useState<EspFirmwareItem[]>([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedFirmware, setSelectedFirmware] = useState<EspFirmwareItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [espType, setEspType] = useState<string | null>(null);

  const loadFirmwares = async () => {
    setLoading(true);

    try {
      const response = await ArtifactsApi.espFirmwares.getList(1, FRONT_FILTER_PAGE_SIZE) as EspFirmwaresResponse;
      setFirmwares(FirmwaresUtils.getItems<EspFirmwareItem>(response));
    } catch (error) {
      console.error("Ошибка получения ESP прошивок:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getListErrorMessage(error, "ESP прошивок")), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFirmwares().then();
  }, []);

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(1);
  };

  const handleEspTypeChange = (value: string | null) => {
    setEspType(value);
    setPage(1);
  };

  const espTypeOptions = useMemo(() => {
    const values = new Set([...ESP_TYPE_OPTIONS.map(({value}) => value),
      ...firmwares.map(({espType}) => espType).filter((value): value is string => Boolean(value))]);
    return Array.from(values).map((value) => ({
      value,
      label: ESP_TYPE_OPTIONS.find((option) => option.value === value)?.label || value,
    }));
  }, [firmwares]);

  const filteredFirmwares = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return firmwares.filter((firmware) => {
      const matchesEspType = !espType || firmware.espType === espType;
      const matchesSearch = !normalizedSearch || FirmwaresUtils.getSearchString(firmware).toLowerCase().includes(normalizedSearch);

      return matchesEspType && matchesSearch;
    });
  }, [firmwares, searchValue, espType]);

  const paginatedFirmwares = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredFirmwares.slice(start, start + PAGE_SIZE);
  }, [filteredFirmwares, page]);

  const handleOpenCreateModal = () => {
    setSelectedFirmware(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEditModal = (firmware: EspFirmwareItem) => {
    setSelectedFirmware(firmware);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFirmware(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    loadFirmwares().then();
  };

  return (
    <>
      <div className="row mt-3 align-items-center firmwares-toolbar">
        <div className="col-md-1 col-sm-2 mb-3">
          <Whisper trigger="click" placement="top" speaker={<Tooltip>Добавить новую ESP прошивку</Tooltip>}>
            <Button
              className="esp-firmwares__add"
              size="md"
              appearance="primary"
              color="green"
              onClick={handleOpenCreateModal}
            >
              <FaPlusIcon />
            </Button>
          </Whisper>
        </div>

        <div className="col-md-4 col-sm-10 mb-3">
          <InputGroup>
            <InputGroup.Addon>
              <AiOutlineSearchIcon />
            </InputGroup.Addon>
            <Input
              placeholder="Поиск ESP прошивок..."
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                setPage(1);
              }}
            />
            {searchValue && (
              <InputGroup.Button onClick={handleClearSearch}>
                <AiOutlineCloseIcon />
              </InputGroup.Button>
            )}
          </InputGroup>
        </div>

      </div>

      <div className="mb-3">
        <RadioTileGroup
          as="div"
          aria-label="Фильтр по типу ESP"
          className="w-100 firmwares-esp-filter-group"
          value={espType ?? ""}
          onChange={(value) => handleEspTypeChange(value === "" ? null : value as string)}
        >
          <RadioTile value="" label="Все">
            <span className="radio-tile-text">Все</span>
          </RadioTile>

          {espTypeOptions
            .map((option) => (
              <RadioTile
                key={option.value}
                value={option.value}
                label={option.label}
              >
                <span className="radio-tile-text">{option.label}</span>
              </RadioTile>
            ))}
        </RadioTileGroup>
      </div>

      <EspFirmwareCheck models={espTypeOptions.map(({value}) => value)} />

      <Table
        height={FIRMWARE_TABLE_HEIGHT}
        data={paginatedFirmwares}
        loading={loading}
        onRowClick={(rowData: EspFirmwareItem) => handleOpenEditModal(rowData)}
        className="users-table firmwares-table"
        rowHeight={60}
        wordWrap
      >
        <Column width={100} sortable verticalAlign="middle" fullText>
          <HeaderCell>Версия</HeaderCell>
          <Cell dataKey="version">
            {(rowData: EspFirmwareItem) => rowData.version || "-"}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" fullText>
          <HeaderCell>Устройство</HeaderCell>
          <Cell dataKey="espType">
            {(rowData: EspFirmwareItem) => FirmwaresUtils.formatEspType(rowData.espType)}
          </Cell>
        </Column>

        <Column width={350} sortable verticalAlign="middle">
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description">
            {(rowData: EspFirmwareItem) => <FirmwareDescriptionCell description={rowData.description} />}
          </Cell>
        </Column>

        <Column width={110} verticalAlign="middle" fullText>
          <HeaderCell>Файл</HeaderCell>
          <Cell>
            {(rowData: EspFirmwareItem) => rowData.fileDescriptor?.name || "-"}
          </Cell>
        </Column>

        <Column width={120} verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Размер</HeaderCell>
          <Cell>
            {(rowData: EspFirmwareItem) => FirmwaresUtils.formatFileSize(rowData.fileDescriptor?.size)}
          </Cell>
        </Column>

        <Column width={70} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Test</HeaderCell>
          <Cell dataKey="isDeployedToTest">
            {(rowData: EspFirmwareItem) => <FirmwaresUtils.DeployStateIcon enabled={rowData.isDeployedToTest} />}
          </Cell>
        </Column>

        <Column width={70} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">One</HeaderCell>
          <Cell dataKey="isDeployedToProduction">
            {(rowData: EspFirmwareItem) => <FirmwaresUtils.DeployStateIcon enabled={rowData.isDeployedToProduction} />}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" fullText>
          <HeaderCell>Создал</HeaderCell>
          <Cell dataKey="createdBy">
            {(rowData: EspFirmwareItem) => rowData.createdBy || "-"}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Создано</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: EspFirmwareItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>

      <div className="d-flex justify-content-end mt-3">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={["total", "-", "pager"]}
          total={filteredFirmwares.length}
          limit={PAGE_SIZE}
          activePage={page}
          onChangePage={setPage}
        />
      </div>

      <EspFirmwareModal
        open={modalOpen}
        mode={modalMode}
        firmware={selectedFirmware}
        onClose={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default EspFirmwares;
